import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import {
  KALENDAR_ORIGIN,
  kalendarEmbedUrl,
  trackBookingCalendarViewed,
  trackBookingCompleted,
} from '../../lib/bookingCta';
import { hashedEmailUserData } from '../../lib/signupUserData';

/**
 * "Book a call" without leaving the app.
 *
 * Opens the ops-set Kalendar booking page (AccessSettings.salesBookingUrl) in
 * a dialog over the page the person is on — the waitlist wall, or an
 * unavailable-template slide — and closes back to it. It is the same
 * calendar the landing's /go/book and /get-started embed, in the same brand
 * frame, with the signed-in user's name and email prefilled.
 *
 * Why a dialog and not a link: a link (to kalendar.work, or to the landing's
 * /go/book) takes them out of the app, and a booking made on Kalendar's own
 * page can only be counted if Kalendar's after-booking redirect happens to
 * land them on a page of ours. Here Kalendar posts `booking_success` to this
 * window the moment the booking is made, so it is counted here, on this
 * surface, with its CTA — the same `booking_completed` + `Schedule` pushes
 * the landing makes, which every ad platform's "Booking Completed"
 * conversion fires from (GTM container GTM-55TWFHZB, loaded on the app too).
 *
 * Tracking (lib/bookingCta.ts): booking_calendar_viewed on open;
 * booking_completed + Schedule (with the hashed email, for enhanced
 * conversions) on Kalendar's booking_success, once; then the caller's
 * `onBooked`, for the server-side record. Only messages from
 * https://kalendar.work are trusted. Kalendar's `kalendar_redirect` message
 * is ignored on purpose — nobody leaves the app — and the iframe is
 * unmounted the instant the booking is reported, because Kalendar's own
 * success screen counts down five seconds and then navigates, inside the
 * frame, to the account's redirect URL (the landing's /booking-confirmed),
 * which would count the booking a second time.
 */
export interface BookCallModalProps {
  /** The ops-set booking page. Must be a Kalendar page (lib/bookingCta.ts
   *  `isKalendarBookingUrl`); callers open anything else in a new tab. */
  bookingUrl: string;
  /** Registered CTA name of the button that opened it (docs/analytics/cta-sources.md on the landing). */
  cta: string;
  /** The signed-in user — name/email are prefilled into Kalendar's form, and
   *  the email is the enhanced-conversions fallback when Kalendar reports none. */
  user?: { name?: string | null; email?: string | null } | null;
  onClose: () => void;
  /** Called once, after the booking is tracked. */
  onBooked?: (detail: { eventType: string }) => void;
}

/** The part of Kalendar's `booking_success` message this dialog reads. */
interface KalendarMessage {
  type?: unknown;
  data?: { eventType?: unknown; attendeeEmail?: unknown } | null;
}

const TITLE_ID = 'book-call-modal-title';

// Styled to the founding-landing design system, like UnavailablePickPane —
// this dialog speaks for the brand, and Kalendar's dark theme sits flush in it.
const BG = '#0d0f0d';
const INK = '#f5f7f1';
const MUTED = '#9da39a';
const HAIRLINE = '#262b25';
const PRIMARY = '#2850D2';
const PRIMARY_HOVER = '#203FAB';
const SPECTRUM = 'linear-gradient(to right, #2f69fa, #6d49e7, #a04ebd, #db538c, #f1567b, #fe813b)';

const BookCallModal: React.FC<BookCallModalProps> = ({ bookingUrl, cta, user, onClose, onBooked }) => {
  const userName = user?.name || '';
  const userEmail = user?.email || '';
  const src = useMemo(
    () => kalendarEmbedUrl(bookingUrl, cta, { name: userName, email: userEmail }),
    [bookingUrl, cta, userName, userEmail],
  );
  const [booked, setBooked] = useState<{ eventType: string } | null>(null);
  const bookedRef = useRef(false);
  // Read through refs inside the effects so an inline arrow from the caller
  // never re-subscribes the listener (and never misses a message in between).
  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Not a Kalendar page: nothing to embed, nothing to listen for. Callers
  // guard with isKalendarBookingUrl; this just makes sure no empty dialog
  // can ever be left on screen.
  useEffect(() => {
    if (!src) onCloseRef.current();
  }, [src]);

  useEffect(() => {
    if (src) trackBookingCalendarViewed(cta);
  }, [src, cta]);

  useEffect(() => {
    if (!src) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== KALENDAR_ORIGIN) return;
      const msg = event.data as KalendarMessage | null;
      if (!msg || typeof msg !== 'object' || msg.type !== 'booking_success') return;
      if (bookedRef.current) return;
      bookedRef.current = true;

      const detail = msg.data && typeof msg.data === 'object' ? msg.data : {};
      const eventType =
        typeof detail.eventType === 'string' && detail.eventType.trim() ? detail.eventType.trim() : 'unknown';
      // First the swap: the iframe goes before Kalendar's in-frame countdown
      // can navigate anywhere.
      setBooked({ eventType });

      // The email the booking was made with, else the account's.
      const email =
        typeof detail.attendeeEmail === 'string' && detail.attendeeEmail.trim() ? detail.attendeeEmail : userEmail;
      void (async () => {
        const userData = await hashedEmailUserData(email);
        trackBookingCompleted({ cta, eventType, userData });
        onBookedRef.current?.({ eventType });
      })();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src, cta, userEmail]);

  if (!src) return null;

  return (
    <Modal
      onClose={onClose}
      labelledBy={TITLE_ID}
      width="min(96vw, 1040px)"
      panelStyle={{ background: BG, border: `1px solid ${HAIRLINE}` }}
    >
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: SPECTRUM }} />
      <div
        className="flex flex-col gap-3 px-4 pb-4 pt-5 sm:px-6 sm:pb-6"
        style={{ color: INK, fontFamily: 'DM Sans, sans-serif', WebkitFontSmoothing: 'antialiased' }}
      >
        {booked ? (
          <div
            className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center"
            role="status"
            aria-live="polite"
            data-testid="book-call-booked"
          >
            <h2
              id={TITLE_ID}
              className="m-0"
              style={{ fontSize: 'clamp(26px, 3vw, 36px)', fontWeight: 400, letterSpacing: '-0.04em', lineHeight: 1.1 }}
            >
              You&apos;re booked.
            </h2>
            <p className="m-0 text-[15px]" style={{ color: MUTED, lineHeight: 1.6 }}>
              Check your inbox for the calendar invite and call details.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 inline-flex items-center justify-center rounded-lg px-6 py-2.5 text-[14px] font-semibold leading-5 text-white transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B9CEFF]"
              style={{ background: PRIMARY, border: `1px solid ${PRIMARY}`, minHeight: 44, minWidth: 120 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = PRIMARY_HOVER; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = PRIMARY; }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* One row (heading left, confirmation right) so the calendar gets the height. */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 pr-12">
              <h2
                id={TITLE_ID}
                className="m-0"
                style={{ fontSize: 'clamp(20px, 2.2vw, 28px)', fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1.15 }}
              >
                Choose a time to talk
              </h2>
              {userEmail && (
                <p className="m-0 text-[13px] sm:text-[14px]" style={{ color: MUTED }}>
                  Confirmation will be sent to {userEmail}
                </p>
              )}
            </div>
            <KalendarEmbed src={src} />
          </>
        )}
      </div>
    </Modal>
  );
};

export default BookCallModal;

// Kalendar's desktop booking card (calendar + time slots, with its own
// padding) needs about this much room. In a shorter dialog the embed is
// scaled down so the whole calendar is visible without scrolling inside the
// frame. Below Kalendar's 768px breakpoint it switches to its mobile layout,
// which is meant to scroll, so it is never scaled there. Same numbers as the
// landing's KalendarFrame, so the two embeds behave alike.
const KALENDAR_NEEDS = { width: 960, height: 620 };
const KALENDAR_MOBILE_BREAKPOINT = 768;
const KALENDAR_MIN_SCALE = 0.75;
// Kalendar fetches the month's availability just after its page loads; the
// loading overlay is held a moment longer so the days appear already marked.
const KALENDAR_AVAILABILITY_GRACE_MS = 1000;
// Safety net only — the overlay can never get stuck over the calendar.
const LOADING_SAFETY_MS = 12000;

function KalendarEmbed({ src }: { src: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setLoaded(true), LOADING_SAFETY_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scale =
    box.width >= KALENDAR_MOBILE_BREAKPOINT
      ? Math.max(KALENDAR_MIN_SCALE, Math.min(1, box.height / KALENDAR_NEEDS.height, box.width / KALENDAR_NEEDS.width))
      : 1;

  return (
    <div
      ref={frameRef}
      className="relative overflow-hidden rounded-[16px]"
      style={{
        background: BG,
        border: `1px solid ${HAIRLINE}`,
        height: 'min(680px, calc(100dvh - 170px))',
        minHeight: 420,
      }}
    >
      <iframe
        src={src}
        title="Schedule a meeting with Founding.dev"
        data-testid="book-call-frame"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          border: 'none',
          width: box.width ? box.width / scale : '100%',
          height: box.height ? box.height / scale : '100%',
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: '0 0',
        }}
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        onLoad={() => setTimeout(() => setLoaded(true), KALENDAR_AVAILABILITY_GRACE_MS)}
      />
      <div
        className={`bc-loading${loaded ? ' is-done' : ''}`}
        role="status"
        aria-live="polite"
        aria-hidden={loaded}
      >
        <span className="bc-spinner" aria-hidden="true" />
        <span>Loading available times…</span>
      </div>
      <style>{`
        .bc-loading {
          position: absolute; inset: 0; z-index: 1;
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
          background: ${BG}; color: ${MUTED}; font-size: 14px;
          transition: opacity 0.35s ease, visibility 0s linear 0s;
        }
        .bc-loading.is-done { opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.35s ease, visibility 0s linear 0.35s; }
        .bc-spinner {
          width: 36px; height: 36px; border-radius: 50%;
          background: conic-gradient(from 0deg, transparent 0deg, #2f69fa 70deg, #6d49e7 150deg, #db538c 240deg, #fe813b 330deg, transparent 360deg);
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
          mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
          animation: bc-spin 0.9s linear infinite;
        }
        @keyframes bc-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .bc-spinner { animation: none; } }
      `}</style>
    </div>
  );
}
